import { BatchDeterminationContext } from '../../../contexts/BatchDeterminationContext';
import { FileContext } from '../../../contexts/FileContext';
import { File } from '../../../interfaces/File';
import { AdvancedOptions, MappingInfo } from '../../../interfaces/Mapping';
import { Template } from '../../../interfaces/Templates';
import { IMPORT_PROCESS_STATE } from '../../../shared/constants/ImportPageConstants';
import { CUSTOM_FORMAT } from '../../../shared/constants/constants';
import './ImportProcess.scss';
import CalcErrorsImport from './calcErrorsImport/CalcErrorsImport';
import FileUpload from './fileUpload/FileUpload';
import ImportSummary from './importSummary/ImportSummary';
import MappingStepPage from './mappingStepPage/MappingStepPage';
import ProcessingImport from './processingImport/ProcessingImport';
import TemplatesSlider from './templatesSlider/TemplatesSlider';
import { SovosButton, SovosCheckbox, SovosDivider, SovosTheme } from 'mosaic-react';
import { Dispatch, SetStateAction, useContext } from 'react';

const ImportPageContent = (props: {
	selectedTemplate: Template;
	setSelectedTemplate: Dispatch<SetStateAction<Template>>;
	processStatus: number;
	setProcessStatus: Dispatch<SetStateAction<number>>;
	mappedColumns: MappingInfo[];
	setMappedColumns: (value: MappingInfo[]) => void;
	allTemplatesFetched: Template[];
	setAllTemplatesFetched: Dispatch<SetStateAction<Template[]>>;
	advancedOptions: AdvancedOptions;
	setAdvancedOptions: Dispatch<SetStateAction<AdvancedOptions>>;
	selectedCustomTemplate: Template;
	setSelectedCustomTemplate: Dispatch<SetStateAction<Template>>;
	columnCardsToHide: MappingInfo[];
	globalMappingData: MappingInfo[];
	onDisableBeginProcessingButton: (value: boolean) => void;
	onFetchingTargets: (value: boolean) => void;
	onPreviewFail: (value: boolean) => void;
	columnsHeaders: MappingInfo[];
	setColumnsHeaders: Dispatch<SetStateAction<MappingInfo[]>>;
	processId: string;
	setProcessId: Dispatch<SetStateAction<string>>;
	importTemplate: Template;
	checkboxSelected: boolean;
	setCheckboxSelected: Dispatch<SetStateAction<boolean>>;
	isAudit: boolean;
	onGlobalTemplateEditSelect: (value: Template) => void;
	handleUploadStepClick: () => void;
	isPreparingMapping: boolean;
	isFetchingTargets: boolean;
	handleMatchingStepClick: () => void;
	isDisableBeginProcessingButton: boolean;
	haveExtraRows: boolean;
}) => {
	const {
		selectedTemplate,
		processStatus,
		setProcessStatus,
		setSelectedTemplate,
		mappedColumns,
		allTemplatesFetched,
		selectedCustomTemplate,
		advancedOptions,
		setMappedColumns,
		setAllTemplatesFetched,
		setSelectedCustomTemplate,
		setAdvancedOptions,
		columnCardsToHide,
		globalMappingData,
		onDisableBeginProcessingButton,
		onFetchingTargets,
		onPreviewFail,
		columnsHeaders,
		setColumnsHeaders,
		onGlobalTemplateEditSelect,
		processId,
		importTemplate,
		checkboxSelected,
		setCheckboxSelected,
		isAudit,
		handleUploadStepClick,
		isPreparingMapping,
		isFetchingTargets,
		handleMatchingStepClick,
		isDisableBeginProcessingButton,
		haveExtraRows,
	} = props;

	const { selectedTransaction } = useContext(BatchDeterminationContext);
	const { selectedFiles, deleteFile } = useContext(FileContext);
	const file: File = {
		id: selectedFiles[0]?.id || selectedTransaction?.fileGuid || '',
		name: selectedFiles[0]?.name || selectedTransaction?.fileName || '',
	};

	const handleOnPrevious = () => {
		setColumnsHeaders([]);
		setMappedColumns([]);
		deleteFile(file.id);
		setProcessStatus(IMPORT_PROCESS_STATE.UPLOADING);
	};

	const renderContent = (processStatus: number): JSX.Element => {
		switch (processStatus) {
			case IMPORT_PROCESS_STATE.UPLOADING:
				return (
					<>
						<h5 className='subtitle'>
							1. Download your tax data from an e-commerce platform or from your company's system.
						</h5>

						<h5 className='subtitle'>2. Select a template</h5>

						<TemplatesSlider
							setSelectedTemplate={setSelectedTemplate}
							onGlobalTemplateEditSelect={template => onGlobalTemplateEditSelect(template)}
						/>

						{selectedTemplate.id !== '' && selectedTemplate.id !== CUSTOM_FORMAT && (
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<SovosCheckbox checked={checkboxSelected} onChange={() => setCheckboxSelected(!checkboxSelected)} />
								<span style={{ fontSize: 14 }}>Customize Template</span>
							</div>
						)}

						<h5 className='subtitle' style={{ marginBottom: 15 }}>
							3. Upload your file
						</h5>
						<FileUpload />
						<p style={{ fontSize: 10, color: SovosTheme.palette.grey[500] }}>Max file size is 50 MB.</p>

						<SovosDivider style={{ marginTop: 40, marginBottom: 40 }} />

						<div style={{ display: 'flex', justifyContent: 'center' }}>
							<SovosButton
								onClick={handleUploadStepClick}
								size='large'
								disabled={file.id === '' || selectedTemplate.id === '' || isPreparingMapping}
							>
								Next
							</SovosButton>
						</div>
					</>
				);
			case IMPORT_PROCESS_STATE.MATCHING:
				return (
					<MappingStepPage
						processStatus={processStatus}
						mappedColumns={mappedColumns}
						allTemplatesFetched={allTemplatesFetched}
						selectedCustomTemplate={selectedCustomTemplate}
						advancedOptions={advancedOptions}
						setMappedColumns={setMappedColumns}
						setAllTemplatesFetched={setAllTemplatesFetched}
						setSelectedCustomTemplate={setSelectedCustomTemplate}
						setAdvancedOptions={setAdvancedOptions}
						selectedGlobalTemplate={selectedTemplate}
						columnCardsToHide={columnCardsToHide}
						globalMappingData={globalMappingData}
						onDisableBeginProcessingButton={onDisableBeginProcessingButton}
						onPreviewFail={onPreviewFail}
						onFetchingTargets={onFetchingTargets}
						columnsHeaders={columnsHeaders}
						setColumnsHeaders={setColumnsHeaders}
						showSnackbar={() => null}
						isFetchingTargets={isFetchingTargets}
						handleMatchingStepClick={handleMatchingStepClick}
						isDisableBeginProcessingButton={isDisableBeginProcessingButton}
						haveExtraRows={haveExtraRows}
						handleOnPrevious={handleOnPrevious}
						setCheckboxSelected={setCheckboxSelected}
					/>
				);
			case IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT:
				return (
					<MappingStepPage
						processStatus={processStatus}
						mappedColumns={mappedColumns}
						allTemplatesFetched={allTemplatesFetched}
						selectedCustomTemplate={selectedCustomTemplate}
						advancedOptions={advancedOptions}
						setMappedColumns={setMappedColumns}
						setAllTemplatesFetched={setAllTemplatesFetched}
						setSelectedCustomTemplate={setSelectedCustomTemplate}
						setAdvancedOptions={setAdvancedOptions}
						selectedGlobalTemplate={selectedTemplate}
						columnCardsToHide={columnCardsToHide}
						globalMappingData={globalMappingData}
						onPreviewFail={onPreviewFail}
						onFetchingTargets={onFetchingTargets}
						columnsHeaders={columnsHeaders}
						setColumnsHeaders={setColumnsHeaders}
						onDisableBeginProcessingButton={onDisableBeginProcessingButton}
						showSnackbar={() => null}
						isFetchingTargets={isFetchingTargets}
						handleMatchingStepClick={handleMatchingStepClick}
						isDisableBeginProcessingButton={isDisableBeginProcessingButton}
						haveExtraRows={haveExtraRows}
						handleOnPrevious={handleOnPrevious}
						setCheckboxSelected={setCheckboxSelected}
					/>
				);
			case IMPORT_PROCESS_STATE.PROCESSING:
				return <ProcessingImport processId={processId} setProcessStatus={setProcessStatus} isAudit={isAudit} />;

			case IMPORT_PROCESS_STATE.COMPLETE:
				return (
					<ImportSummary
						processId={processId}
						importTemplate={importTemplate}
						isAudit={isAudit}
						file={file}
						showSnackbar={() => null}
					/>
				);
			case IMPORT_PROCESS_STATE.COMPLETE_WITH_ERRORS:
				return (
					<CalcErrorsImport
						processId={processId}
						processStatus={processStatus}
						importTemplate={importTemplate}
						isAudit={isAudit}
						file={file}
						showSnackbar={() => null}
					/>
				);
			case IMPORT_PROCESS_STATE.ERROR:
				return (
					<CalcErrorsImport
						processId={processId}
						processStatus={processStatus}
						importTemplate={importTemplate}
						isAudit={isAudit}
						file={file}
						showSnackbar={() => null}
					/>
				);
			case IMPORT_PROCESS_STATE.RETRY:
				return (
					<CalcErrorsImport
						processId={processId}
						processStatus={processStatus}
						importTemplate={importTemplate}
						isAudit={isAudit}
						file={file}
						showSnackbar={() => null}
					/>
				);
			case IMPORT_PROCESS_STATE.MAX_LIMIT_EXCEEDED:
				return (
					<CalcErrorsImport
						processId={processId}
						processStatus={processStatus}
						importTemplate={importTemplate}
						isAudit={isAudit}
						file={file}
						showSnackbar={() => null}
					/>
				);
			default:
				return <></>;
		}
	};

	return <div style={{ marginLeft: 20, marginTop: 12 }}>{renderContent(processStatus)}</div>;
};

export default ImportPageContent;
